import * as React from 'react';
import ActionButton from '@baidu/icloud-ui-action-button';
import { Table as OTable } from '@osui/ui';
import cs from 'classnames';
import { ARRAY_UNIQUE_TAG } from '@formily/core/lib/models';
import { cloneDeep } from 'lodash';
import { useValidateBeforeAdd } from '../../hooks';
import { Field, useFieldContext } from '../../Other';
import { prefixCls } from '../../utils';
import { operatorRender } from './colunmRender';
import { TableField } from './TableField';
import './Table.less';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

const useTable = props => {
  var _value$length, _props$min, _props$max, _state$value$length, _state$value;

  const {
    mutators,
    state
  } = useFieldContext();
  const value = state.value;
  const len = (_value$length = value === null || value === void 0 ? void 0 : value.length) !== null && _value$length !== void 0 ? _value$length : 0;
  const min = (_props$min = props.min) !== null && _props$min !== void 0 ? _props$min : 0;
  const max = (_props$max = props.max) !== null && _props$max !== void 0 ? _props$max : Infinity;
  const disableRemove = len <= min;
  const disableAdd = len >= max;
  const defaultTableState = React.useMemo(() => {
    var _pagination$current, _pagination$pageSize;

    const {
      pagination
    } = props;

    if (pagination === false) {
      return {
        current: 1,
        pageSize: 10
      };
    }

    return { ...pagination,
      current: (_pagination$current = pagination === null || pagination === void 0 ? void 0 : pagination.current) !== null && _pagination$current !== void 0 ? _pagination$current : 1,
      pageSize: (_pagination$pageSize = pagination === null || pagination === void 0 ? void 0 : pagination.pageSize) !== null && _pagination$pageSize !== void 0 ? _pagination$pageSize : 10
    }; // 只设置初始值
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [tableState, setTableState] = React.useState(defaultTableState);

  const onTableChange = pagination => {
    setTableState(pagination);
  };

  const valueLen = (_state$value$length = (_state$value = state.value) === null || _state$value === void 0 ? void 0 : _state$value.length) !== null && _state$value$length !== void 0 ? _state$value$length : 0;
  const {
    current,
    pageSize
  } = tableState; // 调整当前分页

  React.useEffect(() => {
    const baseIndex = (current - 1) * pageSize;

    if (baseIndex >= valueLen) {
      setTableState(s => {
        return { ...s,
          current: Math.max(s.current - 1, 1)
        };
      });
    }
  }, [valueLen, current, pageSize]);
  const columns = React.useMemo(() => {
    if (!props.columns) {
      return undefined;
    }

    const {
      current,
      pageSize
    } = tableState;
    const res = props.columns.map((v, columnIndex) => {
      return { ...v,
        dataIndex: v.title || columnIndex,
        render: (_, __, index) => {
          const fieldIndex = (current - 1) * pageSize + index; // 这里防止下面的渲染出错，所以就不渲染。因为在矫正current的时候，会返回空

          if (fieldIndex >= valueLen) {
            return null;
          }

          const render = v.render || operatorRender(disableRemove);
          return /*#__PURE__*/_jsx(Field, {
            hideUi: true,
            name: fieldIndex,
            children: render({
              index: fieldIndex,
              tableMutators: mutators
            })
          });
        }
      };
    });
    return res;
  }, [disableRemove, mutators, props.columns, tableState, valueLen]);
  return {
    columns,
    value: state.value,
    mutators,
    disableAdd,
    onTableChange,
    pagination: props.pagination === false || !props.pagination ? false : { ...props.pagination,
      ...tableState
    }
  };
};

export function Table(props) {
  // 这里剔除了value和onChange
  const {
    className,
    min,
    max,
    hideAdd,
    addText,
    value: valueIn,
    onChange,
    pagination: paginationIn,
    addButtonProps,
    validateBeforeAdd,
    addButtonPosition,
    ...rest
  } = props;
  const {
    columns,
    mutators,
    value,
    disableAdd,
    pagination,
    onTableChange
  } = useTable(props);
  const {
    canAdd
  } = useValidateBeforeAdd(validateBeforeAdd);
  const add = React.useCallback(async () => {
    if (!(await canAdd())) {
      return;
    } // 这里深拷贝一下，要不然会一直用那个默认值


    const defaultVal = cloneDeep(props.itemDefaultValue) || {};
    mutators.push(defaultVal);
  }, [canAdd, mutators, props.itemDefaultValue]);
  const buttonContent = React.useMemo(() => {
    var _addButtonProps$type;

    if (hideAdd) {
      return null;
    }

    return /*#__PURE__*/_jsx(ActionButton, {
      className: cs(`${prefixCls}-table-add`, {
        [`${prefixCls}-table-add-top`]: addButtonPosition === 'top'
      }),
      disabled: disableAdd,
      action: "create",
      onClick: add,
      ...addButtonProps,
      type: addButtonProps ? (_addButtonProps$type = addButtonProps.type) !== null && _addButtonProps$type !== void 0 ? _addButtonProps$type : 'link' : 'link',
      children: addText
    });
  }, [add, addButtonPosition, addButtonProps, addText, disableAdd, hideAdd]);
  return /*#__PURE__*/_jsxs("div", {
    className: cs(`${prefixCls}-table`, className),
    children: [addButtonPosition === 'top' && buttonContent, /*#__PURE__*/_jsx(OTable, {
      pagination: pagination,
      ...rest,
      onChange: onTableChange,
      dataSource: value,
      columns: columns
    }), addButtonPosition === 'bottom' && buttonContent]
  });
}
Table.displayName = "Table";
Table.defaultProps = {
  addText: '添加',
  validateBeforeAdd: true,
  rowKey: r => {
    return r[ARRAY_UNIQUE_TAG];
  },
  addButtonPosition: 'bottom'
};
Table.Field = TableField;
//# sourceMappingURL=es/components/Table/Table.js.map